import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import { MdClose, MdPlayArrow } from "react-icons/md";

import { checkFileType } from "../../helpers/helpers";

/**
 * A video among an order's attachments (the warehouse app's parcel videos,
 * app 0.7.0): kind "video" from Bolt, or a video file's name for older rows.
 */
export const isVideoAttachment = (attachment) =>
  attachment?.kind === "video" || checkFileType(attachment?.file ?? "") === "Video";

/**
 * A video attachment as a thumbnail (its first frame where the browser can
 * show it, with a play badge): click to play it in a large player (HTML5
 * video with controls, preload="metadata"), with a link to download it for a
 * format this browser can't play (e.g. an iPhone's HEVC .mov on Windows).
 */
export const VideoTile = ({ src, label = "Video", className = "", style }) => {
  const [open, setOpen] = useState(false);
  if (!src) return null;
  return (
    <>
      <button
        type="button"
        aria-label={`Play ${label}`}
        title={label}
        onClick={() => setOpen(true)}
        className={`relative inline-flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border border-ui-border bg-[#111827] p-0 ${className}`}
        style={style}
      >
        <video
          src={`${src}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none h-full w-full object-cover"
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-black/60 text-white">
            <MdPlayArrow aria-hidden="true" className="h-[16px] w-[16px]" />
          </span>
        </span>
      </button>
      {open && <VideoPlayer src={src} label={label} onClose={() => setOpen(false)} />}
    </>
  );
};

export const VideoPlayer = ({ src, label = "Video", onClose }) => (
  <Modal open onClose={onClose} aria-label={label} sx={{ zIndex: 1500 }}>
    <div className="absolute left-1/2 top-1/2 flex max-h-[95vh] w-[min(960px,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col gap-2 rounded-lg bg-black p-2 outline-none">
      <div className="flex items-center gap-2 text-white">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{label}</span>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="text-[12px] text-white underline"
        >
          Download
        </a>
        <button
          type="button"
          aria-label="Close the video"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10"
        >
          <MdClose aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>
      <video
        src={src}
        controls
        autoPlay
        playsInline
        preload="metadata"
        className="max-h-[80vh] w-full rounded bg-black"
      >
        <a href={src} target="_blank" rel="noopener noreferrer">
          Download the video
        </a>
      </video>
    </div>
  </Modal>
);

export default VideoTile;
